import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Table from "@mui/material/Table";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  exportQrPayments,
  getQrPayments,
  getQrRefundOutstanding,
  verifyQrPayment,
} from "../../services";
import ExportButton from "../ExportButton";
import { formatDateTimeBkk } from "../../utils/date";
import { useUserRole } from "../../utils/hooks/useUserRole";
import {
  QrPaymentListItem,
  QrRefundOutstanding,
  getQrStatusLabel,
  isPaidAfterCancel,
  isUnclaimed,
  isAwaitingRefund,
  needsRecheck,
  refundBlockedReason,
  refundConfirmBlockedReason,
  reissueBlockedReason,
} from "../../types/qr-payment.type";
import QrTransactionFilter, { ALL_TIME_PRESET } from "./Filter";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import RefundDialog, { RefundDialogMode } from "./RefundDialog";
import ReissueDialog from "./ReissueDialog";

const LIST_LIMIT = [10, 20, 50, 100];
const API_DATE_FORMAT = "YYYY-MM-DD";

const money = (value: string | null): string =>
  value === null ? "—" : Number(value).toLocaleString("th-TH", { minimumFractionDigits: 2 });

/**
 * What needs a person, and how loudly.
 *
 * Ordered by how wrong the books are, not by how much money is involved. A QR paid twice
 * comes first: the bill is not closed AND the customer has paid more than they owe, so it is
 * the only row here that is two problems at once. `PAID_AFTER_CANCEL` outranks `UNCLAIMED`
 * because its money has no sale left to belong to, while unclaimed money usually has a bill
 * waiting to be matched to it.
 *
 * The duplicate flag carries an icon as well as a colour. Accounting scan this table down
 * the status column, and a row that has to be acted on today should be findable without
 * reading any of the words.
 */
function queueFlag(
  row: QrPaymentListItem,
): { label: string; color: "error" | "warning"; icon?: ReactElement } | null {
  if (row.status === "DUPLICATE_PAYMENT") {
    return {
      label: "จ่ายซ้ำ — ต้องคืนเงินส่วนเกิน",
      color: "error",
      icon: <ErrorOutlineIcon />,
    };
  }
  if (isPaidAfterCancel(row)) {
    return { label: "ยกเลิกแล้วแต่มีเงินเข้า", color: "error" };
  }
  if (isUnclaimed(row)) return { label: "จ่ายแล้วไม่มีบิล", color: "warning" };
  return null;
}

const HEADERS = [
  "ออก QR",
  "สาขา",
  "สถานะ",
  "ยอด",
  "เงินเข้าจริง",
  "เวลาเงินเข้า",
  "ผู้โอน",
  "อ้างอิง (ref1)",
  "อ้างอิง (ref2)",
  "บิล",
];

export default function QrTransactionTable() {
  const [rows, setRows] = useState<QrPaymentListItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [outstanding, setOutstanding] = useState<QrRefundOutstanding | null>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [[dateFrom, dateTo], setDateRange] = useState<[Dayjs, Dayjs]>(() =>
    ALL_TIME_PRESET.range(),
  );
  const [status, setStatus] = useState("all");
  const [queue, setQueue] = useState("all");
  const [branchUuid, setBranchUuid] = useState("all");
  // One dialog serves both halves of a refund, so the target carries which half it is for.
  const [refundTarget, setRefundTarget] = useState<
    { row: QrPaymentListItem; mode: RefundDialogMode } | null
  >(null);
  const [reissueTarget, setReissueTarget] = useState<QrPaymentListItem | null>(null);

  /**
   * Who may order money sent back, and who may sign off that it went. Deliberately narrower
   * than who may read this screen, and it mirrors the server's own cohort — ADMIN reads every
   * branch here precisely because it can move none of it, so it is absent from this list and
   * present in the filter's.
   *
   * Hiding the column is a courtesy. The server refuses the call regardless of what reaches it.
   */
  const {
    isBranchAccounting,
    isBranchFinance,
    isBranchFinanceAndAccount,
    isHeadOfficeAccounting,
    isMaster,
  } = useUserRole();
  const canRefund = Boolean(
    isBranchAccounting ||
      isBranchFinanceAndAccount ||
      isHeadOfficeAccounting ||
      isMaster,
  );

  /**
   * Who may hand a customer a working code again. Wider than `canRefund` and narrower than who
   * reads this screen, mirroring the server's follow-up cohort: chasing an unpaid sale is
   * ordinary collections work, sending money back is not.
   *
   * Finance is in here and not in `canRefund` for exactly that reason. ADMIN is in neither.
   */
  const canReissue = Boolean(
    isBranchAccounting ||
      isBranchFinance ||
      isBranchFinanceAndAccount ||
      isHeadOfficeAccounting ||
      isMaster,
  );
  // Asking the bank again is the same follow-up cohort as reissuing — the server gates both
  // on the same roles — so it is deliberately the same flag rather than a second list that
  // could drift away from it.
  const canRecheck = canReissue;
  const canAct = canRefund || canReissue || canRecheck;

  /**
   * The server takes one `YYYY-MM-DD,YYYY-MM-DD` string and ranges over when the QR was
   * RAISED, not when it was paid.
   */
  const createdDate = useMemo(
    () => `${dateFrom.format(API_DATE_FORMAT)},${dateTo.format(API_DATE_FORMAT)}`,
    [dateFrom, dateTo],
  );

  // One filter object for the page and its export, so the file is exactly what the
  // table would page through.
  const filters = useMemo(
    () => ({
      status: status === "all" ? undefined : status,
      queue: queue === "all" ? undefined : queue,
      branch_uuid: branchUuid === "all" ? undefined : branchUuid,
      created_date: createdDate,
    }),
    [status, queue, branchUuid, createdDate],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getQrPayments({ ...filters, page, limit });
      setRows(data?.rows ?? []);
      setCount(data?.count ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  /**
   * The reconciliation figure, loaded beside the list rather than derived from it: the list
   * is one page of one filter, and the question this answers — how much money have we
   * ordered back that has not gone back — is about all of it, on every page, at any date.
   *
   * Silent on failure. It is a figure alongside the work, not the work itself, and a toast
   * about it would land on someone in the middle of a refund who cannot act on it.
   */
  const loadOutstanding = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getQrRefundOutstanding(branchUuid === "all" ? undefined : branchUuid);
      setOutstanding(data ?? null);
    } catch {
      setOutstanding(null);
    }
  }, [branchUuid]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadOutstanding();
  }, [loadOutstanding]);

  // Any narrowing invalidates the page number: page 5 of the old result set is usually
  // past the end of the new one, which reads as "no results" for a filter that has some.
  useEffect(() => {
    setPage(1);
  }, [status, queue, branchUuid, createdDate, limit]);

  const handleChangeLimit = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
  };

  const handleChangeDateRange = (from: Dayjs, to: Dayjs) => setDateRange([from, to]);

  return (
    <>
      {/* Above the filters, not inside the table: it is the reason someone came to this
          screen at the end of a day, and it is true of every page and every filter. */}
      {outstanding !== null && outstanding.count > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setQueue("REFUND_REQUESTED")}>
              ดูรายการ
            </Button>
          }
        >
          สั่งคืนเงินแล้วแต่ยังไม่ได้โอนคืน {outstanding.count} รายการ รวม{" "}
          {money(outstanding.total_amount)} บาท — ยอดนี้ออกจากเงินโอนแล้ว แต่ยังอยู่ในบัญชีธนาคาร
        </Alert>
      )}

      <QrTransactionFilter
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChangeDateRange={handleChangeDateRange}
        status={status}
        setStatus={setStatus}
        queue={queue}
        setQueue={setQueue}
        branchUuid={branchUuid}
        setBranchUuid={setBranchUuid}
      />

      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ExportButton
          request={() => exportQrPayments(filters)}
          fallbackFilename={`qr-transactions_${dayjs().format("YYYYMMDD")}.xlsx`}
          disabled={count === 0}
        />
      </Box>

      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell />
                {HEADERS.map((header) => (
                  <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>
                    {header}
                  </TableCell>
                ))}
                {canAct && <TableCell align="right">จัดการ</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={HEADERS.length + (canAct ? 2 : 1)} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={HEADERS.length + (canAct ? 2 : 1)} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      ไม่พบรายการ
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const flag = queueFlag(row);
                  return (
                    <TableRow key={row.uuid} hover>
                      {/* A severity stripe rather than a tinted row: the flag has to survive
                          being read on a projector and by someone who cannot separate the
                          two tints. */}
                      <TableCell sx={{ p: 0, width: 4, borderLeft: flag ? 4 : 0, borderColor: flag ? `${flag.color}.main` : "transparent" }} />
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTimeBkk(row.created_at)}
                      </TableCell>
                      <TableCell>{row.branch_code ?? "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                          <Typography variant="body2">{getQrStatusLabel(row.status)}</Typography>
                          {flag && (
                            <Chip
                              size="small"
                              color={flag.color}
                              label={flag.label}
                              icon={flag.icon}
                            />
                          )}
                          {/* Not a queue flag: it says the row is already someone's job, which
                              is the opposite of asking for attention. */}
                          {isAwaitingRefund(row) && (
                            <Chip size="small" variant="outlined" color="info" label="รอคืนเงิน" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{money(row.amount)}</TableCell>
                      <TableCell align="right">{money(row.paid_amount)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {/* Falls back to the confirmation: a QR cancelled with its bill never
                            takes the PAID status, so paid_at can be empty on a row the bank
                            has definitely reported. */}
                        {formatDateTimeBkk(row.paid_at ?? row.confirmation_received_at) || "—"}
                      </TableCell>
                      <TableCell>{row.payer_name ?? "—"}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {row.ref1}
                      </TableCell>
                      {/* ref2 carries the bill number SCB echoes back, so it is the
                          column accounting match a statement line against. */}
                      <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {row.ref2 ?? "—"}
                      </TableCell>
                      <TableCell>{row.bill_uuid ? "มี" : "ไม่มี"}</TableCell>
                      {canAct && (
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Box display="flex" gap={1} justifyContent="flex-end" alignItems="center">
                            {/* Same rule as the buttons beside it: shown only on the rows it
                                is the answer to — the ones the bank has reported and the
                                figures never reached. */}
                            {canRecheck && needsRecheck(row) && (
                              <RecheckCell row={row} onChecked={load} />
                            )}
                            {/* Only rendered where it could ever apply. A disabled "ออก QR
                                ใหม่" on all 20 rows of a normal page is noise that trains
                                people to stop reading the column. */}
                            {canReissue && row.status === "AWAITING_SETTLEMENT" && (
                              <ReissueCell row={row} onReissue={() => setReissueTarget(row)} />
                            )}
                            {canRefund && (
                              <RefundCells
                                row={row}
                                onPick={(mode) => setRefundTarget({ row, mode })}
                              />
                            )}
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="space-between" alignItems="center" p={2} flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2">แสดง</Typography>
            <Select size="small" value={limit} onChange={handleChangeLimit}>
              {LIST_LIMIT.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Typography variant="body2">รายการ · ทั้งหมด {count}</Typography>
          </Box>
          <Pagination
            count={Math.max(1, Math.ceil(count / limit))}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </Box>
      </Paper>

      <ReissueDialog
        row={reissueTarget}
        onClose={() => setReissueTarget(null)}
        // Not closed here: the dialog is showing the new code, which is the only place it is
        // ever displayed. Closing on success would raise a QR at the bank and hide it.
        onReissued={load}
      />

      <RefundDialog
        row={refundTarget?.row ?? null}
        mode={refundTarget?.mode ?? "request"}
        onClose={() => setRefundTarget(null)}
        onDone={() => {
          setRefundTarget(null);
          // The banner is a figure about money, and the press just moved money into or out of
          // what it counts. Leaving it stale would have it contradict the row underneath it.
          loadOutstanding();
          // Reloaded rather than patched in place: both halves move the row on — an order puts
          // it in the waiting state, a confirmation retires it — and a row that still reads its
          // old state beside a "done" message is the one thing that would make an operator
          // press again.
          load();
        }}
      />
    </>
  );
}

/**
 * "Ask the bank again" for a row whose money the bank has already announced.
 *
 * An icon rather than a labelled button: it sits beside two buttons that move money, on the
 * minority of rows that need it, and a third word in that column would compete with them.
 * The tooltip carries what the row is actually waiting for — without it a lone refresh icon
 * reads as "reload the table", which is the one thing it does not do.
 *
 * Disabled while in flight, because the answer takes a round trip to SCB and a second press
 * only asks the bank the same question twice.
 */
function RecheckCell({
  row,
  onChecked,
}: Readonly<{ row: QrPaymentListItem; onChecked: () => void }>) {
  const [checking, setChecking] = useState(false);

  const check = async () => {
    setChecking(true);
    try {
      await verifyQrPayment(row.uuid);
      // Reloaded rather than patched: a check that finds the money moves the row out of
      // this state entirely — new status, new figures, and no button here any more.
      onChecked();
    } finally {
      setChecking(false);
    }
  };

  return (
    <Tooltip title="ธนาคารแจ้งว่ามีเงินเข้าแล้ว แต่ยังดึงยอดไม่ได้ — กดเพื่อตรวจสอบอีกครั้ง">
      <span>
        <IconButton
          size="small"
          color="primary"
          aria-label="ตรวจสอบอีกครั้ง"
          disabled={checking}
          onClick={check}
        >
          {checking ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * The replace-code control, and — when it is unavailable — why.
 *
 * Same reasoning as the refund controls: a disabled button with no reason reads as a broken screen.
 * The commonest reason here is the good one — the customer's existing code has not expired
 * yet — and saying so stops an operator raising a second payable code out of impatience.
 */
function ReissueCell({
  row,
  onReissue,
}: Readonly<{ row: QrPaymentListItem; onReissue: () => void }>) {
  const blocked = reissueBlockedReason(row);
  const button = (
    <Button size="small" variant="outlined" disabled={Boolean(blocked)} onClick={onReissue}>
      ออก QR ใหม่
    </Button>
  );
  return blocked ? (
    <Tooltip title={blocked}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

/**
 * The two refund controls, and — when either is unavailable — why.
 *
 * A disabled button with no reason is the same as no button: the operator is left to guess
 * whether the row cannot be refunded or the screen is broken. The tooltip carries the reason,
 * and it wraps a `<span>` because MUI cannot hang one off a disabled button directly.
 *
 * Both are rendered on every row rather than swapped one for the other, so the column reads
 * the same way down the page and the second half of the job is visibly a second step. The
 * ordinary row shows an available "สั่งคืนเงิน" beside a disabled "ยืนยันคืนแล้ว" that says
 * why — which is also how an operator learns the order the two are meant to happen in.
 */
function RefundCells({
  row,
  onPick,
}: Readonly<{ row: QrPaymentListItem; onPick: (mode: RefundDialogMode) => void }>) {
  const requestBlocked = refundBlockedReason(row);
  const confirmBlocked = refundConfirmBlockedReason(row);

  return (
    <>
      <ActionButton
        label="สั่งคืนเงิน"
        blocked={requestBlocked}
        color="warning"
        onClick={() => onPick("request")}
      />
      <ActionButton
        label="ยืนยันคืนแล้ว"
        blocked={confirmBlocked}
        color="error"
        onClick={() => onPick("confirm")}
      />
    </>
  );
}

/** A small outlined action that explains its own absence. */
function ActionButton({
  label,
  blocked,
  color,
  onClick,
}: Readonly<{
  label: string;
  blocked: string | null;
  color: "warning" | "error";
  onClick: () => void;
}>) {
  const button = (
    <Button
      size="small"
      color={color}
      variant="outlined"
      disabled={Boolean(blocked)}
      onClick={onClick}
    >
      {label}
    </Button>
  );
  return blocked ? (
    <Tooltip title={blocked}>
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}
